import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Container, Row, Col, Card, Form, Table, Button, Spinner, Badge, InputGroup, Modal, ListGroup, Tabs, Tab, OverlayTrigger, Tooltip as BootstrapTooltip } from "react-bootstrap";
import Pagination from "../components/Pagination";
import moment from "moment";
import { getLLMCostStats, getLLMCostHistory, getLLMCostTenantSummaries, getAllTenants } from "../context/functions/AdminFunction";
import { FiActivity, FiChevronDown, FiChevronLeft, FiChevronRight, FiCpu, FiDollarSign, FiSearch, FiX, FiInfo } from "react-icons/fi";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import "../styles/llm-cost-analysis.css";
import { THIRD_PARTY_SERVICES } from "../utils/constants";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const AVERAGE_STAT_CONFIG = [
  {
    key: "total",
    averageLabel: "Average Cost Per Tenant",
    singleTenantLabel: "Tenant Cost",
    description: "Overall cost metrics across all LLM operations."
  },
  {
    key: "document_analysis",
    averageLabel: "Document analysis",
    singleTenantLabel: "Document analysis",
    description: "Initial classification of uploaded documents by type and owner before deeper analysis."
  },
  {
    key: "profile_report",
    averageLabel: "Profile report",
    singleTenantLabel: "Profile report",
    description: "Deep analysis of documents (forensics) and data extraction used to generate the tenant report."
  },
  {
    key: "profile_summary",
    averageLabel: "Profile summary",
    singleTenantLabel: "Profile summary",
    description: "Costs of the final summary generated from a tenant's full profile for quick review."
  },
  {
    key: "tink_report",
    averageLabel: "Tink report",
    singleTenantLabel: "Tink report",
    description: "Analysis of bank data retrieved via Tink. Expected to decrease as this moves to code-based processing."
  },
  {
    key: "risk_recommendation_report",
    averageLabel: "Risk recommendation",
    singleTenantLabel: "Risk recommendation",
    description: "Cost of the LLM used in the recommendation engine to explain decisions after selecting multiple tenants."
  },
  {
    key: "chat",
    averageLabel: "Chat",
    singleTenantLabel: "Chat",
    description: "Costs from landlords questions sent to the AI chatbot within the product for that specific tenant."
  },
  {
    key: "experian",
    averageLabel: "Experian check",
    singleTenantLabel: "Experian check",
    description: "Cost of every default check we do. Right now the cost is around 7500€ per 2500 checks per year, which can go down to 1€ if we increase volume"
  },
  {
    key: "tink",
    averageLabel: "Bank connections (Tink)",
    singleTenantLabel: "Bank connections (Tink)",
    description: "Cost of every Bank connection made with Tink (Bank statements do not count)."
  },
  {
    key: "clave",
    averageLabel: "Cl@ve documents",
    singleTenantLabel: "Cl@ve documents",
    description: "Cost of every cl@ve document downloaded using Permiss"
  },
  {
    key: "entrust",
    averageLabel: "KYC / AML (Entrust)",
    singleTenantLabel: "KYC / AML (Entrust)",
    description: "Cost of every KYC/AML check we do using Entrust."
  }
];

const CATEGORY_FILTER_OPTIONS = [
  { value: "", label: "All Categories" },
  { value: "__llm", label: "LLM Costs" },
  { value: "__third_party", label: "Third-party Usage" },
  { value: "document_analysis", label: "Document Analysis" },
  { value: "profile_report", label: "Profile Report" },
  { value: "profile_summary", label: "Profile Summary" },
  { value: "tink_report", label: "Tink Report" },
  { value: "risk_recommendation_report", label: "Risk Recommendation Report" },
  { value: "chat", label: "Chat" },
  { value: "experian", label: "Experian" },
  { value: "tink", label: "Tink (Bank)" },
  { value: "clave", label: "Cl@ve" },
  { value: "entrust", label: "Entrust (KYC/AML)" }
];

const LLM_FILTER_VALUE = "__llm";
const THIRD_PARTY_FILTER_VALUE = "__third_party";

const THIRD_PARTY_SERVICE_CONFIG = {
  experian: {
    label: "Experian check",
    unitLabel: "checks",
    description: "Cost of every default check we do. Right now the cost is around 7500€ per 2500 checks per year, which can go down to 1€ if we increase volume"
  },
  entrust: {
    label: "KYC / AML (Entrust)",
    unitLabel: "responses",
    description: "Cost of every KYC/AML check we do using Entrust."
  },
  clave: {
    label: "Cl@ve documents",
    unitLabel: "docs",
    description: "Cost of every cl@ve document downloaded using Permiss"
  },
  tink: {
    label: "Bank connections (Tink)",
    unitLabel: "connections",
    description: "Cost of every Bank connection made with Tink (Bank statements do not count)."
  }
};

const THIRD_PARTY_SERVICE_COSTS = Object.values(THIRD_PARTY_SERVICES).reduce((acc, service) => {
  acc[service.name] = service.cost;
  return acc;
}, {});

const formatTitle = (value = "") => {
  return value.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
};

const getRecordEntityId = (record, key) => {
  const value = record[key];
  if (!value) return "";
  if (typeof value === "string") return value;
  return value._id || value.$oid || "";
};

const DATE_PRESET_OPTIONS = [
  { value: "", label: "All Time" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "custom", label: "Custom" },
];

const LLMCostAnalysis = () => {
  const [stats, setStats] = useState({
    averages: {},
    dailyTrend: [],
    topSpenders: [],
    rangePeriods: {
      weekCount: 1,
      monthCount: 1
    }
  });
  const [tenantSummaries, setTenantSummaries] = useState([]);
  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingTenantSummaries, setLoadingTenantSummaries] = useState(false);

  // Tenant Search State
  const [tenantSearchTerm, setTenantSearchTerm] = useState("");
  const [tenantResults, setTenantResults] = useState([]);
  const [isSearchingTenants, setIsSearchingTenants] = useState(false);
  const [showTenantDropdown, setShowTenantDropdown] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const searchRef = useRef(null);

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [selectedTenantSummary, setSelectedTenantSummary] = useState(null);
  const [modalTab, setModalTab] = useState("stats");
  const [modalStats, setModalStats] = useState({ averages: {} });
  const [loadingModalStats, setLoadingModalStats] = useState(false);
  const [modalHistory, setModalHistory] = useState([]);
  const [loadingModalHistory, setLoadingModalHistory] = useState(false);
  const [modalPage, setModalPage] = useState(1);
  const [modalTotalPages, setModalTotalPages] = useState(1);

  // Pagination & Sorting
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState("cost");
  const [sortOrder, setSortOrder] = useState("desc");
  const limit = 10;

  // Filters
  const [filters, setFilters] = useState({
    userId: "",
    category: "",
    llmProvider: "",
    datePreset: "",
    startDate: "",
    endDate: "",
    groupBy: "Tenant"
  });
  const [isLLMBreakdownExpanded, setIsLLMBreakdownExpanded] = useState(false);

  const [debouncedFilters, setDebouncedFilters] = useState(filters);

const getLLMFilters = useCallback((sourceFilters) => {
  const { category, ...rest } = sourceFilters;
  const filters = { ...rest };

  if (category === LLM_FILTER_VALUE) {
    // Return all filters (no category filter)
    return filters;
  } else if (category === THIRD_PARTY_FILTER_VALUE) {
    // Add onlyThirdParty=true
    filters.onlyThirdParty = "true";
    return filters;
  } else if (category) {
    // Specific category (could be LLM or third-party)
    if (["experian", "tink", "clave", "entrust"].includes(category)) {
      filters.onlyThirdParty = "true";
      filters.service = category.toUpperCase();
    } else {
      filters.category = category;
    }
  }

  return filters;
}, []);

  // Filter Synchronization Logic
  useEffect(() => {
    // Primary effect to handle IMMEDIATE updates for selection-based filters
    const handleImmediateFilters = () => {
      const isCategoryChange = filters.category !== debouncedFilters.category;

      if (!isCategoryChange) {
        setDebouncedFilters({ ...filters });
        setPage(1);
      } else {
        const timer = setTimeout(() => {
          setDebouncedFilters({ ...filters });
          setPage(1);
        }, 600);
        return () => clearTimeout(timer);
      }
    };

    return handleImmediateFilters();
  }, [filters]);

  const fetchStats = async () => {
    setLoadingStats(true);
    try {
      const response = await getLLMCostStats(getLLMFilters(debouncedFilters));

      if (response && response.success) {
        setStats(response.data);
      } else {
        setStats({
          averages: {},
          dailyTrend: [],
          topSpenders: [],
          rangePeriods: {
            weekCount: 1,
            monthCount: 1
          }
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchTenantSummaries = useCallback(async (currentPage = 1) => {
    setLoadingTenantSummaries(true);
    try {
      const response = await getLLMCostTenantSummaries(
        { ...getLLMFilters(debouncedFilters), sortBy, sortOrder },
        currentPage,
        limit
      );
      if (response && response.success) {
        setTenantSummaries(response.data.records || []);
        const total = response.data.pagination?.totalRecords || 0;
        setTotalPages(Math.ceil(total / limit) || 1);
      } else {
        setTenantSummaries([]);
        setTotalPages(1);
      }
    } catch (e) {
      console.error(e);
      setTenantSummaries([]);
      setTotalPages(1);
    } finally {
      setLoadingTenantSummaries(false);
    }
  }, [debouncedFilters, getLLMFilters, limit, sortBy, sortOrder]);

  useEffect(() => {
    document.title = "LLM Cost Analytics | Admin";
  }, []);

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line
  }, [debouncedFilters]);

  useEffect(() => {
    fetchTenantSummaries(page);
    // eslint-disable-next-line
  }, [page, debouncedFilters, sortBy, sortOrder]);

  useEffect(() => {
    if (!showModal || !selectedTenantSummary?._id) {
      return;
    }

    fetchModalStats(selectedTenantSummary._id);
    // eslint-disable-next-line
  }, [showModal, selectedTenantSummary?._id]);

  useEffect(() => {
    if (!showModal || !selectedTenantSummary?._id) {
      return;
    }

    fetchModalHistory(selectedTenantSummary._id, modalPage);
    // eslint-disable-next-line
  }, [showModal, selectedTenantSummary?._id, modalPage, debouncedFilters]);

  // Tenant Search Logic
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (tenantSearchTerm.length >= 2 && !selectedTenant) {
        setIsSearchingTenants(true);
        try {
          const res = await getAllTenants(1, tenantSearchTerm, "");
          if (res && res.tenants) {
            setTenantResults(res.tenants);
            setShowTenantDropdown(true);
          }
        } catch (err) {
          console.error("Tenant search error:", err);
        } finally {
          setIsSearchingTenants(false);
        }
      } else {
        setTenantResults([]);
        setShowTenantDropdown(false);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [tenantSearchTerm, selectedTenant]);

  const getDateRangeForPreset = (preset, offset = 0) => {
    if (preset === "week") {
      const base = moment().add(offset, "weeks");
      return {
        startDate: base.clone().startOf("isoWeek").format("YYYY-MM-DD"),
        endDate: base.clone().endOf("isoWeek").format("YYYY-MM-DD"),
      };
    }

    if (preset === "month") {
      const base = moment().add(offset, "months");
      return {
        startDate: base.clone().startOf("month").format("YYYY-MM-DD"),
        endDate: base.clone().endOf("month").format("YYYY-MM-DD"),
      };
    }

    return {
      startDate: "",
      endDate: "",
    };
  };

  // Click outside search listener
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowTenantDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectTenant = (tenant) => {
    setSelectedTenant(tenant);
    setTenantSearchTerm(`${tenant.fname || ""} ${tenant.lname || ""}`.trim() || tenant.email);
    setFilters(prev => ({ ...prev, userId: tenant._id }));
    setShowTenantDropdown(false);
  };

  const clearTenantFilter = () => {
    setSelectedTenant(null);
    setTenantSearchTerm("");
    setFilters(prev => ({ ...prev, userId: "" }));
  };

  // Sync tenant filter state when text is cleared manually
  useEffect(() => {
    if (tenantSearchTerm === "" && (selectedTenant || filters.userId)) {
      setSelectedTenant(null);
      setFilters(prev => ({ ...prev, userId: "" }));
    }
  }, [tenantSearchTerm, selectedTenant, filters.userId]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === "datePreset") {
      if (!value) {
        setFilters(prev => ({ ...prev, datePreset: "", startDate: "", endDate: "" }));
        return;
      }

      if (value === "custom") {
        setFilters(prev => ({ ...prev, datePreset: "custom", startDate: "", endDate: "" }));
        return;
      }

      const { startDate, endDate } = getDateRangeForPreset(value);

      setFilters(prev => ({ ...prev, datePreset: value, startDate, endDate }));
      return;
    }

    if (name === "startDate" || name === "endDate") {
      setFilters(prev => ({ ...prev, [name]: value, datePreset: "custom" }));
      return;
    }

    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handlePageClick = (event) => {
    setPage(event.selected + 1);
  };

  const handleRefresh = () => {
    fetchStats();
    fetchTenantSummaries(page);
  };

  const fetchModalStats = useCallback(async (tenantId) => {
    setLoadingModalStats(true);
    try {
      const response = await getLLMCostStats({ userId: tenantId });
      if (response && response.success) {
        setModalStats(response.data || { averages: {} });
      } else {
        setModalStats({ averages: {} });
      }
    } catch (e) {
      console.error(e);
      setModalStats({ averages: {} });
    } finally {
      setLoadingModalStats(false);
    }
  }, []);

  const fetchModalHistory = useCallback(async (tenantId, currentPage = 1) => {
    setLoadingModalHistory(true);
    try {
      const response = await getLLMCostHistory({ ...debouncedFilters, userId: tenantId }, currentPage, limit);
      if (response && response.success) {
        setModalHistory(response.data.records || []);
        const total = response.data.pagination?.totalRecords || 0;
        setModalTotalPages(Math.ceil(total / limit) || 1);
      } else {
        setModalHistory([]);
        setModalTotalPages(1);
      }
    } catch (e) {
      console.error(e);
      setModalHistory([]);
      setModalTotalPages(1);
    } finally {
      setLoadingModalHistory(false);
    }
  }, [debouncedFilters, limit]);

  const handleOpenModal = (tenantSummary) => {
    setSelectedTenantSummary(tenantSummary);
    setModalTab("stats");
    setModalPage(1);
    setModalStats({ averages: {} });
    setModalHistory([]);
    setShowModal(true);
  };

  const handleShiftPeriod = (direction) => {
    if (!["week", "month"].includes(filters.datePreset) || !filters.startDate) {
      return;
    }

    const unit = filters.datePreset === "week" ? "weeks" : "months";
    const startDate = moment(filters.startDate).add(direction, unit);
    const endDate = moment(filters.endDate).add(direction, unit);

    setFilters(prev => ({
      ...prev,
      startDate: startDate.format("YYYY-MM-DD"),
      endDate: endDate.format("YYYY-MM-DD"),
    }));
  };

  const averageTotalStats = stats.averages?.total || {};
  const isSingleTenantView = Boolean(filters.userId);
  const summaryCards = isSingleTenantView
    ? [
      {
        label: "Tenant Cost",
        value: `€${(averageTotalStats.averageCostPerTenant || 0).toFixed(4)}`,
        meta: "Selected tenant",
        icon: <FiDollarSign />,
        color: "rgba(40, 167, 69, 0.15)"
      },
      {
        label: "Total Calls",
        value: `${averageTotalStats.totalCalls || 0}`,
        meta: "Selected range",
        icon: <FiCpu />,
        color: "rgba(40, 167, 69, 0.15)"
      }
    ]
    : [
      {
        label: "Total Cost",
        value: `€${(averageTotalStats.totalUsdCost || 0).toFixed(4)}`,
        meta: "Selected range",
        icon: <FiDollarSign />,
        color: "rgba(37, 56, 55, 0.05)"
      },
      {
        label: "Average Cost Per Tenant",
        value: `€${(averageTotalStats.averageCostPerTenant || 0).toFixed(4)}`,
        meta: `${averageTotalStats.tenantCount || 0} tenants`,
        icon: <FiDollarSign />,
        color: "rgba(40, 167, 69, 0.15)"
      },
      {
        label: "Total Tenants",
        value: `${averageTotalStats.tenantCount || 0}`,
        meta: "Selected range",
        icon: <FiActivity />,
        color: "rgba(37, 56, 55, 0.05)"
      },
      {
        label: "Total Calls",
        value: `${averageTotalStats.totalCalls || 0}`,
        meta: "Selected range",
        icon: <FiCpu />,
        color: "rgba(40, 167, 69, 0.15)"
      }
    ];

  const averageStatCards = AVERAGE_STAT_CONFIG
    .filter(({ key }) => key !== "total")
    .filter(({ key }) => {
      if (!filters.category) return true;
      if (filters.category === LLM_FILTER_VALUE) return !["experian", "tink", "clave", "entrust"].includes(key);
      if (filters.category === THIRD_PARTY_FILTER_VALUE) return ["experian", "tink", "clave", "entrust"].includes(key);
      return key === filters.category;
    })
    .map(({ key, averageLabel, singleTenantLabel, description }) => {
      const isThirdParty = ["experian", "tink", "clave", "entrust"].includes(key);
      const unitLabel = isThirdParty ? "events" : "calls";
      
      const fallbackCost = stats.dailyTrend
        ?.filter(t => t._id?.category?.toLowerCase() === key.toLowerCase())
        .reduce((acc, curr) => acc + (curr.cost || 0), 0) || 0;

      const fallbackCount = stats.dailyTrend
        ?.filter(t => t._id?.category?.toLowerCase() === key.toLowerCase())
        .length || 0;

      const totalUsdCost = stats.averages?.[key]?.totalUsdCost || fallbackCost;
      const totalCalls = stats.averages?.[key]?.totalCalls || fallbackCount;

      return {
        type: isThirdParty ? "third_party" : "llm_subcategory",
        label: isSingleTenantView ? singleTenantLabel : averageLabel,
        description,
        averageCostPerTenant: stats.averages?.[key]?.averageCostPerTenant || 0,
        totalUsdCost,
        tenantCount: stats.averages?.[key]?.tenantCount || 0,
        totalCalls,
        eventCount: totalCalls,
        unitLabel,
        key
      };
    });

  const llmSubcategoryRows = [...averageStatCards.filter(({ type }) => type === "llm_subcategory")].sort((a, b) => b.totalUsdCost - a.totalUsdCost);
  const thirdPartyDisplayRows = [...averageStatCards.filter(({ type }) => type === "third_party")].sort((a, b) => b.totalUsdCost - a.totalUsdCost);
  const thirdPartyTotalCost = thirdPartyDisplayRows.reduce((acc, curr) => acc + curr.totalUsdCost, 0);
  const thirdPartyTenantCount = thirdPartyDisplayRows.reduce((ids, curr) => ids + curr.tenantCount, 0);
  const thirdPartyEventCount = thirdPartyDisplayRows.reduce((acc, curr) => acc + curr.eventCount, 0);

  const llmTotalRow = {
    key: "llm-total",
    type: "llm_total",
    label: "LLM costs",
    description: AVERAGE_STAT_CONFIG.find(({ key }) => key === "total")?.description || "Overall cost metrics across all LLM operations.",
    totalUsdCost: averageTotalStats.totalUsdCost || llmSubcategoryRows.reduce((acc, curr) => acc + curr.totalUsdCost, 0),
    averageCostPerTenant: averageTotalStats.averageCostPerTenant || 0,
    tenantCount: averageTotalStats.tenantCount || 0,
    eventCount: averageTotalStats.totalCalls || llmSubcategoryRows.reduce((acc, curr) => acc + curr.eventCount, 0),
    unitLabel: "calls",
  };

  const categoryRows = (() => {
    if (filters.category === THIRD_PARTY_FILTER_VALUE) {
      return thirdPartyDisplayRows;
    }

    if (filters.category && filters.category !== LLM_FILTER_VALUE) {
      return [...llmSubcategoryRows, ...thirdPartyDisplayRows];
    }

    const rows = filters.category === LLM_FILTER_VALUE ? [llmTotalRow] : [llmTotalRow, ...thirdPartyDisplayRows];
    if (isLLMBreakdownExpanded) {
      rows.splice(1, 0, ...llmSubcategoryRows);
    }
    return rows;
  })();

  const getCategoryRowDescription = (item) => {
    if (item.description) return item.description;
    return AVERAGE_STAT_CONFIG.find(c => c.key === item.key)?.description || item.label;
  };

  const modalAverageStats = modalStats.averages?.total || {};
  const modalCategoryCards = AVERAGE_STAT_CONFIG
    .filter(({ key }) => key !== "total")
    .map(({ key, singleTenantLabel }) => ({
      key,
      label: singleTenantLabel,
      total: modalStats.averages?.[key]?.averageCostPerTenant || 0,
      totalUsdCost: modalStats.averages?.[key]?.totalUsdCost || 0,
      totalCalls: modalStats.averages?.[key]?.totalCalls || 0
    }));

  const isCustomDateRange = filters.datePreset === "custom";
  const isPeriodNavigatorVisible = filters.datePreset === "week" || filters.datePreset === "month";
  const currentPeriodLabel = filters.datePreset === "week"
    ? `${moment(filters.startDate).format("DD MMM YYYY")} - ${moment(filters.endDate).format("DD MMM YYYY")}`
    : filters.datePreset === "month"
      ? moment(filters.startDate).format("MMMM YYYY")
      : "";

  const getProvidersLabel = (record) => {
    if (!Array.isArray(record.providerBreakdown) || record.providerBreakdown.length === 0) {
      return "N/A";
    }

    return record.providerBreakdown
      .map((provider) => provider.llmProvider)
      .filter(Boolean)
      .join(", ");
  };

  const getDisplayCategory = (record) => {
    let category = record.category || "other";
    if (record.requestPath === "/api/profile-report") category = "profile_report";
    if (record.requestPath === "/api/profile-summary") category = "profile_summary";
    if (record.requestPath === "/api/forensic-report") category = "forensic";
    //Replace underscore with space, make titlecase
    category = category.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
    return category;
  };

  const getTenantName = (tenant) => {
    if (!tenant) return "System / N/A";
    const fullName = `${tenant.fname || ""} ${tenant.lname || ""}`.trim();
    return fullName || tenant.email || "System / N/A";
  };

  const handleModalPageClick = (event) => {
    setModalPage(event.selected + 1);
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "desc" ? "asc" : "desc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  const chartData = useMemo(() => {
    if (!stats.dailyTrend) return { labels: [], datasets: [] };

    let labelDates = [];
    if (filters.startDate && filters.endDate) {
      const start = moment(filters.startDate);
      const end = moment(filters.endDate);
      const diffDays = end.diff(start, 'days');

      // If the range is huge (e.g. > 90 days), maybe fallback to active dates to avoid performance issues
      // But for Week/Month (7-31 days), we definitely want to see all days.
      if (diffDays >= 0 && diffDays < 90) {
        let current = moment(start);
        while (current.isSameOrBefore(end)) {
          labelDates.push(current.format("YYYY-MM-DD"));
          current.add(1, 'day');
        }
      } else {
        labelDates = [...new Set(stats.dailyTrend.map(item => item._id.date))].sort();
      }
    } else {
      labelDates = [...new Set(stats.dailyTrend.map(item => item._id.date))].sort();
    }

    if (labelDates.length === 0) return { labels: [], datasets: [] };

    // Get unique categories present in trend
    const categories = [...new Set(stats.dailyTrend.map(item => item._id.category))].filter(Boolean)

    const datasets = categories.map((cat, idx) => {
      const data = labelDates.map(date => {
        const entry = stats.dailyTrend.find(item => item._id.date === date && item._id.category === cat);
        return entry ? entry.cost : 0;
      });

      // Using Theme Colors: Primary Green, then complementary colors
      const themeColorsArr = ['#a3eb65', '#253837', '#6366f1', '#f87171', '#fbbf24', '#a855f7', '#10b981'];
      return {
        label: cat.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()),
        data: data,
        backgroundColor: themeColorsArr[idx % themeColorsArr.length],
        stack: 'Stack 0',
      };
    });

    return {
      labels: labelDates.map(d => {
        const m = moment(d);
        if (stats.trendGranularity === "month") return m.format("MMM YYYY");
        if (stats.trendGranularity === "week") return `Wk ${m.isoWeek()}, ${m.year()}`;
        return m.year() === moment().year() ? m.format("D MMM") : m.format("D MMM YY");
      }),
      datasets
    };
  }, [stats.dailyTrend, stats.trendGranularity, filters.startDate, filters.endDate]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      datalabels: { display: false },
      legend: {
        position: 'bottom',
        labels: {
          usePointStyle: true,
          pointStyle: 'rectRounded',
          font: { size: 10, weight: '600' },
          padding: 15
        }
      },
      tooltip: {
        callbacks: {
          label: (context) => `€${context.parsed.y.toFixed(4)}`
        }
      }
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: { font: { size: 10 } }
      },
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: {
          font: { size: 10 },
          callback: (value) => `€${value}`
        },
        grid: {
          color: '#f1f5f9',
          drawBorder: false
        }
      }
    }
  };

  const totalCostOverall = (() => {
    if (filters.category === THIRD_PARTY_FILTER_VALUE) return thirdPartyTotalCost;
    if (filters.category && filters.category !== LLM_FILTER_VALUE) {
      const allRows = [...llmSubcategoryRows, ...thirdPartyDisplayRows];
      return allRows.reduce((acc, curr) => acc + curr.totalUsdCost, 0);
    }
    if (filters.category === LLM_FILTER_VALUE) return llmTotalRow.totalUsdCost;
    return llmTotalRow.totalUsdCost + thirdPartyTotalCost;
  })();

  const totalTenantCount = stats.averages?.total?.tenantCount || 0;
  const totalCallsCount = stats.averages?.total?.totalCalls || 0;
  const totalCategoryTenantCount = filters.category === THIRD_PARTY_FILTER_VALUE
    ? thirdPartyTenantCount
    : totalTenantCount;
  const totalCategoryEventCount = filters.category === THIRD_PARTY_FILTER_VALUE
    ? thirdPartyEventCount
    : totalCallsCount + (filters.category ? 0 : thirdPartyEventCount);

  return (
    <div className="llm-cost-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-800 mb-1" style={{ color: "var(--llm-color-text-primary)", fontSize: "22px" }}>LLM cost analytics</h4>
          <p className="text-muted small fw-medium mb-0">Per-tenant cost breakdown for the selected range</p>
        </div>
        <div className="d-flex gap-2">

        </div>
      </div>

      {/* Filters Row */}
      <Card className="llm-card shadow-sm mb-4">
        <Card.Body className="p-3">
          <div className="llm-filter-row">
            <div className="llm-filter-item" style={{ flex: 2 }}>
              <span className="llm-filter-label">Tenant</span>
              <div className="position-relative" ref={searchRef}>
                <InputGroup size="sm">
                  <Form.Control
                    className="border-end-0 ps-3"
                    style={{ height: "38px", borderRadius: "6px 0 0 6px" }}
                    type="text"
                    placeholder="Name or email..."
                    autoComplete="off"
                    value={tenantSearchTerm}
                    onChange={(e) => {
                      const val = e.target.value;
                      setTenantSearchTerm(val);
                      if (selectedTenant || filters.userId !== "") {
                        setSelectedTenant(null);
                        setFilters(prev => ({ ...prev, userId: "" }));
                      }
                    }}
                  />
                  {(tenantSearchTerm || selectedTenant) && (
                    <InputGroup.Text
                      className="bg-white border-start-0 cursor-pointer pe-3"
                      onClick={clearTenantFilter}
                      style={{ borderRadius: "0 6px 6px 0" }}
                    >
                      <FiX className="text-danger" size={14} />
                    </InputGroup.Text>
                  )}
                  {showTenantDropdown && (
                    <ListGroup className="position-absolute w-100 shadow-lg mt-1" style={{ zIndex: 1000, maxHeight: "200px", overflowY: "auto", top: "40px" }}>
                      {tenantResults.map((t) => (
                        <ListGroup.Item key={t._id} action onClick={() => handleSelectTenant(t)} className="py-2">
                          <div className="fw-bold small">{t.fname} {t.lname}</div>
                          <div className="text-muted" style={{ fontSize: "11px" }}>{t.email}</div>
                        </ListGroup.Item>
                      ))}
                      {tenantResults.length === 0 && !isSearchingTenants && (
                        <ListGroup.Item className="text-center text-muted small">No tenants found</ListGroup.Item>
                      )}
                    </ListGroup>
                  )}
                </InputGroup>
              </div>
            </div>

            <div className="llm-filter-item">
              <span className="llm-filter-label">Category</span>
              <Form.Select size="sm" name="category" value={filters.category} onChange={handleFilterChange} style={{ height: "38px", borderRadius: "6px" }}>
                {CATEGORY_FILTER_OPTIONS.map((option) => (
                  <option key={option.value || "all-cat"} value={option.value}>{option.label}</option>
                ))}
              </Form.Select>
            </div>

            <div className="llm-filter-item">
              <span className="llm-filter-label">LLM</span>
              <Form.Select size="sm" name="llmProvider" value={filters.llmProvider} onChange={handleFilterChange} style={{ height: "38px", borderRadius: "6px" }}>
                <option value="">All systems</option>
                <option value="gemini">Gemini</option>
                <option value="openai">OpenAI</option>
              </Form.Select>
            </div>

            <div className={`llm-filter-item ${isCustomDateRange ? "llm-filter-item-wide" : ""}`}>
              <span className="llm-filter-label">Range</span>
              <div className="d-flex gap-2 align-items-center">
                <Form.Select size="sm" name="datePreset" value={filters.datePreset} onChange={handleFilterChange} style={{ height: "38px", borderRadius: "6px", width: "120px" }}>
                  {DATE_PRESET_OPTIONS.map((option) => (
                    <option key={option.value || "range"} value={option.value}>{option.label}</option>
                  ))}
                </Form.Select>
                {isCustomDateRange && (
                  <div className="d-flex gap-1 align-items-center">
                    <Form.Control size="sm" type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} style={{ height: "38px", width: "135px", fontSize: "12px" }} />
                    <span className="text-muted small">-</span>
                    <Form.Control size="sm" type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} style={{ height: "38px", width: "135px", fontSize: "12px" }} />
                  </div>
                )}
              </div>
            </div>

            {/* <div className="llm-filter-item">
              <span className="llm-filter-label">Group by</span>
              <Form.Select size="sm" name="groupBy" value={filters.groupBy} onChange={handleFilterChange} style={{ height: "38px", borderRadius: "6px" }}>
                <option>Tenant</option>
                <option>Day</option>
                <option>Category</option>
                <option>LLM</option>
              </Form.Select>
            </div> */}

            <Button className="llm-btn-apply" onClick={handleRefresh}>Apply</Button>
          </div>
        </Card.Body>
      </Card>

      {isPeriodNavigatorVisible && (
        <div className="llm-period-navigator">
          <button className="llm-nav-btn shadow-none" onClick={() => handleShiftPeriod(-1)}>
            <FiChevronLeft size={20} />
          </button>
          <div className="llm-period-label-container">
            <span className="llm-period-value">{currentPeriodLabel}</span>
            <span className="llm-period-subtext">Selected {filters.datePreset}</span>
          </div>
          <button className="llm-nav-btn shadow-none" onClick={() => handleShiftPeriod(1)}>
            <FiChevronRight size={20} />
          </button>
        </div>
      )}

      {/* Category Table Section */}
      <Card className="llm-card shadow-sm mb-4">
        <Card.Header className="bg-white border-0 py-3 px-4 d-flex justify-content-between align-items-center">
          <h6 className="mb-0 fw-800 text-uppercase" style={{ fontSize: "13px", letterSpacing: "0.05em" }}>Cost by category</h6>
          <span className="text-muted small fw-medium" style={{ fontSize: "11px" }}>Sorted by cost desc</span>
        </Card.Header>
        <div className="table-responsive">
          <Table className="llm-table" hover>
            <thead>
              <tr>
                <th style={{ width: "25%" }}>Category</th>
                <th style={{ width: "15%" }}>Cost</th>
                <th style={{ width: "25%" }}>Share</th>
                <th style={{ width: "15%" }}>Cost / tenant</th>
                <th style={{ width: "10%" }}>Tenants</th>
                <th style={{ width: "10%" }}>Calls / events</th>
              </tr>
            </thead>
            <tbody>
              {loadingStats ? (
                <tr><td colSpan="6" className="text-center py-5"><Spinner size="sm" animation="border" variant="primary" /></td></tr>
              ) : (
                <>
                  {categoryRows.map((item) => {
                    const share = item.totalUsdCost / (totalCostOverall || 1) * 100;
                    const isLLMTotalRow = item.type === "llm_total";
                    const isLLMSubcategoryRow = item.type === "llm_subcategory";
                    return (
                    <tr key={item.key} className={isLLMSubcategoryRow ? "llm-subcategory-row" : ""}>
                      <td>
                        <div className={`llm-category-name category-${item?.key}`}>
                          {isLLMTotalRow && (
                            <button
                              type="button"
                              className={`llm-collapse-toggle ${isLLMBreakdownExpanded ? "is-expanded" : ""}`}
                              onClick={() => setIsLLMBreakdownExpanded(prev => !prev)}
                              aria-label={isLLMBreakdownExpanded ? "Collapse LLM breakdown" : "Expand LLM breakdown"}
                            >
                              <FiChevronDown size={14} />
                            </button>
                          )}
                          <span className={isLLMSubcategoryRow ? "llm-subcategory-label" : ""}>{item.label}</span>
                          <OverlayTrigger
                            placement="top"
                            delay={{ show: 250, hide: 400 }}
                            overlay={<BootstrapTooltip id={`tooltip-${item.key}`}>{getCategoryRowDescription(item)}</BootstrapTooltip>}
                            >
                            <span className="cursor-help"><FiInfo className="llm-info-icon" /></span>
                          </OverlayTrigger>
                            {item.type === "third_party" && (
                              <span className="llm-third-party-badge">3rd party</span>
                            )}
                            {item.type === "llm_total" && (
                              <span className="llm-llm-badge">LLM</span>
                            )}
                        </div>
                      </td>
                      <td className="fw-700">€{item.totalUsdCost.toFixed(4)}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div className="llm-share-bar-container flex-grow-1">
                            <div className="llm-share-bar-fill" style={{ width: `${share.toFixed(1)}%` }}></div>
                          </div>
                          <span className="text-muted small fw-bold" style={{ width: "40px" }}>
                            {share.toFixed(1)}%
                          </span>
                        </div>
                      </td>
                      <td className="text-muted fw-600">€{item.averageCostPerTenant.toFixed(4)}</td>
                      <td className="text-muted">{item.tenantCount}</td>
                      <td className="text-muted">{item.eventCount} {item.unitLabel}</td>
                    </tr>
                    );
                  })}
                  <tr className="llm-total-row">
                    <td><span className="llm-total-label fw-800">TOTAL</span></td>
                    <td className="fw-800">€{totalCostOverall.toFixed(4)}</td>
                    <td className="text-muted small fw-bold">100%</td>
                    <td className="text-muted fw-700">€{(totalCostOverall / (totalCategoryTenantCount || 1)).toFixed(4)}</td>
                    <td className="text-muted">{totalCategoryTenantCount}</td>
                    <td className="text-muted">{totalCategoryEventCount}</td>
                  </tr>
                </>
              )}
            </tbody>
          </Table>
        </div>
      </Card>

      {/* Daily Trend & Top Spenders */}
      <Row className="mb-4">
        <Col lg={8}>
          <Card className="llm-card h-100 shadow-sm border-0">
            <Card.Header className="bg-white border-0 py-3 px-4 d-flex justify-content-between align-items-center">
              <h6 className="mb-0 fw-800 text-uppercase" style={{ fontSize: "13px", letterSpacing: "0.05em" }}>{stats.trendTitle || "Daily cost trend"}</h6>
              <span className="text-muted small fw-medium" style={{ fontSize: "11px" }}>Stacked by category</span>
            </Card.Header>
            <Card.Body className="p-4 pt-1">
              <div className="llm-trend-container">
                {loadingStats ? (
                  <div className="h-100 d-flex align-items-center justify-content-center"><Spinner animation="grow" variant="primary" size="sm" /></div>
                ) : (
                  <Bar data={chartData} options={chartOptions} />
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4}>
          <Card className="llm-card h-100 shadow-sm border-0">
            <Card.Header className="bg-white border-0 py-3 px-4 d-flex justify-content-between align-items-center">
              <h6 className="mb-0 fw-800 text-uppercase" style={{ fontSize: "13px", letterSpacing: "0.05em" }}>Top spenders</h6>
            </Card.Header>
            <Card.Body className="p-4 pt-1">
              {loadingStats ? (
                <div className="py-5 text-center"><Spinner size="sm" animation="border" /></div>
              ) : stats.topSpenders?.length > 0 ? (
                stats.topSpenders.map((spender, i) => (
                  <div key={i} className="llm-top-spender-item">
                    <span className="fw-bold" style={{ color: "var(--llm-color-text-secondary)", fontSize: "14px" }}>{spender.name || "Unknown Tenant"}</span>
                    <span className="fw-800" style={{ fontSize: "14px" }}>€{spender.cost.toFixed(4)}</span>
                  </div>
                ))
              ) : (
                <div className="text-center py-5 text-muted small">No data available</div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tenant Summaries Section */}
      <Card className="llm-card shadow-sm mb-4">
        <Card.Header className="bg-white border-0 py-3 px-4 d-flex justify-content-between align-items-center">
          <h6 className="mb-0 fw-800 text-uppercase" style={{ fontSize: "13px", letterSpacing: "0.05em" }}>Tenant cost summaries</h6>
          <span className="text-muted small fw-medium" style={{ fontSize: "11px" }}>{tenantSummaries.length} rows · click column to sort</span>
        </Card.Header>
        <div className="table-responsive">
          <Table hover className="llm-table">
            <thead>
              <tr>
                <th className="sortable-header" onClick={() => toggleSort("tenant")}>TENANT</th>
                <th className="sortable-header" onClick={() => toggleSort("cost")}>COST {sortBy === 'cost' && (sortOrder === 'desc' ? '↓' : '↑')}</th>
                <th className="sortable-header" onClick={() => toggleSort("calls")}>CALLS {sortBy === 'calls' && (sortOrder === 'desc' ? '↓' : '↑')}</th>
                <th>TOP CATEGORY</th>
                <th className="sortable-header" onClick={() => toggleSort("lastActivity")}>LAST ACTIVITY {sortBy === 'lastActivity' && (sortOrder === 'desc' ? '↓' : '↑')}</th>
                <th className="text-center">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {loadingTenantSummaries ? (
                <tr><td colSpan="6" className="text-center py-5"><Spinner size="sm" animation="border" /></td></tr>
              ) : tenantSummaries.length > 0 ? (
                tenantSummaries.map((record, index) => {
                  const isTopSpender = index === 0 && page === 1 && sortOrder === 'desc' && sortBy === 'cost' && record.totalUsdCost > 0;
                  return (
                    <tr key={record._id}>
                      <td>
                        <div className="llm-tenant-stack">
                          <span className="llm-tenant-name">{getTenantName(record.tenant)}</span>
                          <span className="llm-tenant-email">{record.tenant?.email || "N/A"}</span>
                        </div>
                      </td>
                      <td>
                        <span className={isTopSpender ? "llm-red-pill" : "fw-700"}>
                          €{(record.totalUsdCost || 0).toFixed(4)}
                        </span>
                      </td>
                      <td className="text-muted fw-600">{record.totalCalls || 0}</td>
                      <td>
                        <Badge bg="light" text="dark" className="border px-2 py-1" style={{ fontSize: "11px", borderRadius: "4px" }}>
                          {record.topCategory ? record.topCategory.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) : "N/A"}
                        </Badge>
                      </td>
                      <td className="text-muted small fw-medium">
                        {record.lastUtilizationAt ? moment(record.lastUtilizationAt).format("DD MMM, HH:mm") : "N/A"}
                      </td>
                      <td className="text-center">
                        <Button
                          variant="white"
                          className="border shadow-none py-1 px-3 fw-700"
                          style={{ fontSize: "11px", borderRadius: "6px" }}
                          onClick={() => handleOpenModal(record)}
                        >
                          Details
                        </Button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr><td colSpan="6" className="text-center py-5 text-muted">No tenant cost records found</td></tr>
              )}
            </tbody>
          </Table>
        </div>
        {!loadingTenantSummaries && tenantSummaries.length > 0 && (
          <div className="d-flex justify-content-end p-3 border-top bg-light bg-opacity-50">
            <Pagination
              currentPage={page - 1}
              totalPages={totalPages}
              onPageChange={handlePageClick}
              pageRangeDisplayed={3}
              className="pagination mb-0"
            />
          </div>
        )}
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" centered className="audit-modal">
        <Modal.Header closeButton className="border-0 px-4 pt-4 pb-0">
          <Modal.Title className="fw-bold h5">
            {selectedTenantSummary ? `${getTenantName(selectedTenantSummary.tenant)} Details` : "Tenant Details"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {selectedTenantSummary && (
            <div className="space-y-3">
              <div className="mb-3">
                <Row className="g-3">
                  <Col md={4}>
                    <div className="bg-light p-3 rounded-3 border h-100">
                      <p className="text-muted small mb-1 fw-bold text-uppercase" style={{ fontSize: "11px" }}>Tenant</p>
                      <div className="text-dark fw-bold" style={{ fontSize: "14px" }}>
                        {getTenantName(selectedTenantSummary.tenant)}
                      </div>
                      <small className="text-muted d-block">{selectedTenantSummary.tenant?.email || "No Email Provided"}</small>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="bg-light p-3 rounded-3 border h-100 text-center">
                      <p className="text-muted small mb-1 fw-bold text-uppercase" style={{ fontSize: "11px" }}>Last Utilization On</p>
                      <p className="fw-bold mb-0 text-dark" style={{ fontSize: "13px" }}>
                        {selectedTenantSummary.lastUtilizationAt ? moment(selectedTenantSummary.lastUtilizationAt).format("DD MMM YYYY, HH:mm:ss") : "N/A"}
                      </p>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="bg-light p-3 rounded-3 border h-100 text-center">
                      <p className="text-muted small mb-1 fw-bold text-uppercase" style={{ fontSize: "11px" }}>Total Calls</p>
                      <p className="fw-bold mb-0 text-dark" style={{ fontSize: "13px" }}>{selectedTenantSummary.totalCalls || 0}</p>
                    </div>
                  </Col>
                </Row>
              </div>

              <Tabs activeKey={modalTab} onSelect={(key) => setModalTab(key || "stats")} className="mb-3 llm-cost-tabs">
                <Tab eventKey="stats" title="Lifetime Stats">
                  <div className="pt-3">
                    <Row className="g-3 mb-3">
                      <Col md={6} lg={4}>
                        <Card className="llm-card h-100 shadow-sm border-0">
                          <Card.Body className="p-3">
                            <p className="text-muted mb-2 fw-bold text-uppercase" style={{ letterSpacing: "0.05em", fontSize: "11px" }}>Tenant Cost</p>
                            {loadingModalStats ? <Spinner size="sm" animation="border" /> : <h4 className="mb-0 fw-bold" style={{ color: "var(--llm-color-text-primary)", fontSize: "20px" }}>€{(modalAverageStats.averageCostPerTenant || 0).toFixed(4)}</h4>}
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col md={6} lg={4}>
                        <Card className="llm-card h-100 shadow-sm border-0">
                          <Card.Body className="p-3">
                            <p className="text-muted mb-2 fw-bold text-uppercase" style={{ letterSpacing: "0.05em", fontSize: "11px" }}>Lifetime Total Cost</p>
                            {loadingModalStats ? <Spinner size="sm" animation="border" /> : <h4 className="mb-0 fw-bold" style={{ color: "var(--llm-color-text-primary)", fontSize: "20px" }}>€{(modalAverageStats.totalUsdCost || 0).toFixed(4)}</h4>}
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col md={6} lg={4}>
                        <Card className="llm-card h-100 shadow-sm border-0">
                          <Card.Body className="p-3">
                            <p className="text-muted mb-2 fw-bold text-uppercase" style={{ letterSpacing: "0.05em", fontSize: "11px" }}>Lifetime Total Calls</p>
                            {loadingModalStats ? <Spinner size="sm" animation="border" /> : <h4 className="mb-0 fw-bold" style={{ color: "var(--llm-color-text-primary)", fontSize: "20px" }}>{modalAverageStats.totalCalls || 0}</h4>}
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>

                    <div className="bg-light p-3 rounded-3 border">
                      <p className="text-muted small mb-3 fw-bold text-uppercase" style={{ fontSize: "11px" }}>Category Lifetime Stats</p>
                      <Table responsive borderless className="mb-0 align-middle">
                        <thead>
                          <tr>
                            <th style={{ fontSize: "11px", color: "#64748b" }}>Category</th>
                            <th style={{ fontSize: "11px", color: "#64748b" }}>Tenant Cost</th>
                            <th style={{ fontSize: "11px", color: "#64748b" }}>Total Cost</th>
                            <th style={{ fontSize: "11px", color: "#64748b" }}>Total Calls</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loadingModalStats ? (
                            <tr>
                              <td colSpan="4" className="py-3"><Spinner size="sm" animation="border" /></td>
                            </tr>
                          ) : modalCategoryCards.map((item) => (
                            <tr key={item.key}>
                              <td className="fw-bold">{item.label}</td>
                              <td>€{item.total.toFixed(4)}</td>
                              <td>€{item.totalUsdCost.toFixed(4)}</td>
                              <td>{item.totalCalls}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </Tab>

                <Tab eventKey="logs" title="Historical Execution Logs">
                  <div className="pt-3">
                    <div className="table-responsive">
                      <Table hover className="align-middle mb-0">
                        <thead>
                          <tr>
                            <th className="px-3" style={{ fontSize: "11px", fontWeight: "700", color: "var(--llm-color-text-secondary)" }}>DATE & TIME</th>
                            <th style={{ fontSize: "11px", fontWeight: "700", color: "var(--llm-color-text-secondary)" }}>CATEGORY</th>
                            <th style={{ fontSize: "11px", fontWeight: "700", color: "var(--llm-color-text-secondary)" }}>PROVIDERS</th>
                            <th style={{ fontSize: "11px", fontWeight: "700", color: "var(--llm-color-text-secondary)" }}>UTILIZATION</th>
                            <th style={{ fontSize: "11px", fontWeight: "700", color: "var(--llm-color-text-secondary)" }}>COST</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loadingModalHistory ? (
                            <tr>
                              <td colSpan="5" className="text-center py-5">
                                <Spinner animation="grow" variant="success" size="sm" />
                                <p className="mt-2 text-muted small fw-bold text-uppercase">Synchronizing...</p>
                              </td>
                            </tr>
                          ) : modalHistory.length > 0 ? (
                            modalHistory.map((record) => (
                              <tr key={record?._id?.$oid || record?._id} className="llm-table-row-border">
                                <td className="px-3 py-2">
                                  <span className="fw-bold d-block text-dark" style={{ fontSize: "14px" }}>{moment(record.created_at?.$date || record.created_at).format("DD MMM YYYY")}</span>
                                  <small className="text-muted fw-medium" style={{ fontSize: "12px" }}>{moment(record.created_at?.$date || record.created_at).format("HH:mm:ss")}</small>
                                </td>
                                <td>
                                  <Badge bg="success" style={{ fontSize: "11px", fontWeight: "600" }}>{getDisplayCategory(record)}</Badge>
                                </td>
                                <td>
                                  <div className="fw-bold text-dark text-capitalize" style={{ fontSize: "14px" }}>{getProvidersLabel(record)}</div>
                                  <div className="text-muted" style={{ fontSize: "12px", fontWeight: "500" }}>{record.providerBreakdown?.length || 0} provider entries</div>
                                </td>
                                <td>
                                  <div className="d-flex flex-column">
                                    <span className="fw-bold text-dark" style={{ fontSize: "14px" }}>{record.totalTokens?.toLocaleString() || "0"}</span>
                                    <div className="d-flex gap-2 text-muted" style={{ fontSize: "11px", fontWeight: "600" }}>
                                      <span>{record.totalCalls || 0} calls</span>
                                      <span>↑{record.totalInputTokens || 0}</span>
                                      <span>↓{record.totalOutputTokens || 0}</span>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <Badge bg="success" style={{ fontSize: "12px", fontWeight: "700" }}>€{record.totalUsdCost?.toFixed(5)}</Badge>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="5" className="text-center py-4 text-muted">
                                <FiSearch size={32} className="mb-2 opacity-20" />
                                <h6 className="fw-bold text-dark" style={{ fontSize: "14px" }}>No Historical Logs For This Tenant</h6>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>

                    {!loadingModalHistory && modalHistory.length > 0 && (
                      <div className="d-flex justify-content-end p-3 border-top bg-light bg-opacity-50">
                        <Pagination
                          currentPage={modalPage - 1}
                          totalPages={modalTotalPages}
                          onPageChange={handleModalPageClick}
                          pageRangeDisplayed={3}
                          className="pagination mb-0"
                        />
                      </div>
                    )}
                  </div>
                </Tab>
              </Tabs>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <style>
        {`
          .pagination .page-item .page-link {
            color: var(--llm-color-text-secondary);
            border: 1px solid var(--llm-color-border);
            padding: 6px 12px;
            font-size: 12px;
            font-weight: 600;
          }
          .pagination .page-item.active .page-link {
            background-color: var(--llm-color-primary) !important;
            border-color: var(--llm-color-primary) !important;
            color: var(--llm-color-dark) !important;
          }
          .pagination .page-item:not(.active) .page-link:hover {
            background-color: #f8fafc;
          }
        `}
      </style>
    </div>
  );
};

export default LLMCostAnalysis;
